import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import AddToast from "../../state/actions/AddToast";
import { eventTrack } from "../../common/helpers/helpers";
import { ANALYTICS_CATEGORIES } from "../cards/common/constants";

const ClipboardCopier = ({ children, text }) => {
  const onSuccess = useCallback(() => {
    AddToast("Short link copied to clipboard", "success");
  }, []);

  const onError = useCallback(() => {
    AddToast("Short link not copied to clipboard", "error");
  }, []);

  const fallbackCopy = useCallback(
    (str) => {
      const textArea = document.createElement("textarea");
      textArea.value = str;
      textArea.style.top = "-9999";
      textArea.style.left = "0";
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.select();
      }
      try {
        const result = document.execCommand("copy");
        if (result) {
          onSuccess();
          document.body.removeChild(textArea);
        } else {
          onError();
          document.body.removeChild(textArea);
        }
      } catch (err) {
        onError();
        document.body.removeChild(textArea);
      }
    },
    [onSuccess, onError]
  );

  const copyText = useCallback(() => {
    eventTrack(
      ANALYTICS_CATEGORIES.ANALYSIS_PAGE_HEADER_BUTTONS,
      "Copy Link",
      ""
    );
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(onSuccess, onError);
    } else {
      fallbackCopy(text);
    }
  }, [text, fallbackCopy, onSuccess, onError]);

  const element = useMemo(() => {
    return React.cloneElement(children, { onClick: copyText });
  }, [copyText, children]);

  return element;
};

ClipboardCopier.propTypes = {
  children: PropTypes.node,
  text: PropTypes.string,
};

ClipboardCopier.defaultProps = {
  children: null,
  text: ``,
};

export default React.memo(ClipboardCopier);
